import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Copy,
  ExternalLink,
  LayoutGrid,
  List,
  Lock,
  Plus,
  Target,
} from 'lucide-react';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../hooks/useToast';
import apiService from '../services/api';
import {
  SettingsCard,
  SettingsReadOnlyField,
} from '../components/settings/settingsUi.jsx';
import ProLeadDrawer from '../components/prospection/ProLeadDrawer.jsx';
import AddProspectModal from '../components/prospection/AddProspectModal.jsx';
import Nr2jPlanFollowUpModal from '../components/nr2j/Nr2jPlanFollowUpModal.jsx';
import Nr2jProspectionKanban from '../components/nr2j/Nr2jProspectionKanban.jsx';
import Nr2jOwnerCell from '../components/nr2j/Nr2jOwnerCell.jsx';
import Nr2jOwnerFilterSelect from '../components/nr2j/Nr2jOwnerFilterSelect.jsx';
import { useNr2jOwnerFilter } from '../hooks/useNr2jOwnerFilter.js';
import Nr2jProspectRowMenu, {
  NR2J_KANBAN_STAGES,
  formatProspectNextAction,
} from '../components/nr2j/Nr2jProspectRowMenu.jsx';
import { buildProfessionalsPublicUrl } from '../components/prestation-wizard/buildWizardSiteUrls.js';
import {
  copyProfessionalsPublicUrl,
  professionalsPageLiveBadge,
} from '../utils/sitePublicStatusUi.js';
import {
  formatPercent,
} from '../utils/proLeadSource.js';
import {
  nr2jStageLabel,
  organizationDisplayName,
} from '../utils/nr2jConstants';
import { createNr2jProspectFromModal } from '../utils/nr2jCreateProspect.js';
import { readAdminSessionUser } from '../utils/settingsSessionUser.js';

const EMPTY_ORG_KPIS = {
  nouveaux: 0,
  aRelancer: 0,
  qualifies: 0,
  gagnes: 0,
  perdus: 0,
  prospectsOpen: 0,
  total: 0,
  tauxConversion: 0,
};

const ACTIVE_FILTERS = [
  { id: 'ACTIVE', label: 'Actifs' },
  { id: 'NOUVEAU', label: 'Nouveaux', stages: ['NOUVEAU'] },
  { id: 'A_CONTACTER', label: 'À contacter', stages: ['A_CONTACTER'] },
  { id: 'CONTACTE', label: 'Contactés', stages: ['CONTACTE'] },
  { id: 'QUALIFIE', label: 'Qualifiés', stages: ['QUALIFIE'] },
  { id: 'RDV', label: 'RDV', stages: ['RDV'] },
  { id: 'PROPOSITION', label: 'Proposition', stages: ['PROPOSITION'] },
  { id: 'NEGOCIATION', label: 'Négociation', stages: ['NEGOCIATION'] },
  { id: 'FOLLOWUP', label: 'À relancer' },
];

const HISTORY_FILTERS = [
  { id: 'GAGNE', label: 'Gagnés' },
  { id: 'PERDU', label: 'Perdus' },
  { id: 'ARCHIVED', label: 'Archivés' },
];

const VIEW_STORAGE_KEY = 'nr2j.prospection.view';
const HISTORY_IDS = new Set(HISTORY_FILTERS.map((f) => f.id));

function readStoredView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'kanban' ? 'kanban' : 'list';
  } catch {
    return 'list';
  }
}

function stageBadgeClass(stage) {
  if (stage === 'NOUVEAU' || stage === 'A_CONTACTER') return 'bg-sky-50 text-sky-800 border-sky-200';
  if (stage === 'CONTACTE') return 'bg-indigo-50 text-indigo-800 border-indigo-200';
  if (stage === 'QUALIFIE') return 'bg-violet-50 text-violet-800 border-violet-200';
  if (stage === 'RDV') return 'bg-amber-50 text-amber-900 border-amber-200';
  if (stage === 'PROPOSITION') return 'bg-orange-50 text-orange-900 border-orange-200';
  if (stage === 'NEGOCIATION') return 'bg-rose-50 text-rose-800 border-rose-200';
  if (stage === 'GAGNE') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (stage === 'PERDU') return 'bg-gray-100 text-gray-700 border-gray-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

export default function Prospection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [orgKpis, setOrgKpis] = useState(EMPTY_ORG_KPIS);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadFilter, setLeadFilter] = useState('ACTIVE');
  const [leadBusy, setLeadBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [menuId, setMenuId] = useState(null);
  const [followUpOrg, setFollowUpOrg] = useState(null);
  const [followUpConflict, setFollowUpConflict] = useState(null);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [createTaskOrg, setCreateTaskOrg] = useState(null);
  const [createTaskBusy, setCreateTaskBusy] = useState(false);
  const [createTaskForm, setCreateTaskForm] = useState({ title: '', dueAt: '', notes: '' });
  const [viewMode, setViewMode] = useState(readStoredView);
  const [conversionOpen, setConversionOpen] = useState(false);
  const [moveBusyId, setMoveBusyId] = useState(null);
  const [deleteOrg, setDeleteOrg] = useState(null);
  const ownerFilter = useNr2jOwnerFilter(orgs);
  const ownerParamsRef = useRef(ownerFilter.params);
  ownerParamsRef.current = ownerFilter.params;

  const isHistory = HISTORY_IDS.has(leadFilter);
  const effectiveView = isHistory ? 'list' : viewMode;

  const setView = useCallback((mode) => {
    const next = mode === 'kanban' ? 'kanban' : 'list';
    setViewMode(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const loadPipeline = useCallback(async (filter) => {
    const params = { ...ownerParamsRef.current };
    if (filter === 'ACTIVE') params.activeProspects = true;
    else if (filter === 'FOLLOWUP') params.followUp = true;
    else if (filter === 'ARCHIVED') params.status = 'INACTIVE';
    else if (filter === 'GAGNE') params.stage = 'GAGNE';
    else if (filter === 'PERDU') params.stage = 'PERDU';
    else if (filter === 'NOUVEAU') {
      params.stage = 'NOUVEAU';
    } else {
      const found = ACTIVE_FILTERS.find((f) => f.id === filter);
      if (found?.stages?.[0]) params.stage = found.stages[0];
      else params.activeProspects = true;
    }
    const res = await apiService.listNr2jOrganizations(params);
    let list = res?.organizations || [];
    // FOLLOWUP : serveur applique déjà prospectionFollowUpWhere — ne pas refiltrer par étape.
    const prospectFilters = [
      'ACTIVE',
      'FOLLOWUP',
      'NOUVEAU',
      'A_CONTACTER',
      'CONTACTE',
      'QUALIFIE',
      'RDV',
      'PROPOSITION',
      'NEGOCIATION',
    ];
    if (prospectFilters.includes(filter)) {
      list = list.filter((o) => o.commercialStatus === 'PROSPECT');
    }
    setOrgs(list);
  }, []);

  const loadOrgKpis = useCallback(async () => {
    const res = await apiService.getNr2jProspectionKpis(ownerParamsRef.current);
    setOrgKpis(res?.kpis || EMPTY_ORG_KPIS);
  }, []);

  const reloadAfterPipelineMutation = useCallback(async () => {
    await loadPipeline(leadFilter);
    await loadOrgKpis();
    const session = readAdminSessionUser();
    if (session?.role === 'SUPER_ADMIN') {
      try {
        const leadsRes = await apiService.getProLeads();
        setLeads(leadsRes?.leads || []);
      } catch {
        setLeads([]);
      }
    }
  }, [leadFilter, loadPipeline, loadOrgKpis]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const session = readAdminSessionUser();
      const isSuperAdmin = session?.role === 'SUPER_ADMIN';
      if (isSuperAdmin) {
        const [pagesRes, leadsRes] = await Promise.all([
          apiService.getSitePages().catch(() => ({ pages: [] })),
          apiService.getProLeads().catch(() => ({ leads: [] })),
          loadOrgKpis(),
        ]);
        setPages(pagesRes?.pages || []);
        setLeads(leadsRes?.leads || []);
      } else {
        // NR2J_STAFF : aucun appel SUPER_ADMIN-only (pro-leads / site-pages)
        setPages([]);
        setLeads([]);
        await loadOrgKpis();
      }
      await loadPipeline(leadFilter);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger la prospection');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toast identity; leadFilter via separate effect
  }, [loadPipeline, loadOrgKpis]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'followup') setLeadFilter('FOLLOWUP');
    if (filter === 'archived') setLeadFilter('ARCHIVED');
    const stage = searchParams.get('stage');
    if (stage && ACTIVE_FILTERS.concat(HISTORY_FILTERS).some((f) => f.id === stage)) {
      setLeadFilter(stage);
    }
    const status = searchParams.get('status');
    if (status === 'WON') setLeadFilter('GAGNE');
    if (status === 'LOST') setLeadFilter('PERDU');
  }, [searchParams]);

  useEffect(() => {
    if (loading) return;
    loadPipeline(leadFilter).catch((error) => {
      toast.error(error?.message || 'Impossible de filtrer le pipeline');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadFilter]);

  useEffect(() => {
    if (loading) return;
    Promise.all([loadPipeline(leadFilter), loadOrgKpis()]).catch((error) => {
      toast.error(error?.message || 'Impossible de filtrer par commercial');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerFilter.value]);

  const openOrganization = (orgId) => {
    if (!orgId) return;
    navigate(`/nr2j/clients/${orgId}`);
  };

  const openFromLead = async (lead) => {
    try {
      let orgId = lead.organizationId;
      if (!orgId) {
        const res = await apiService.ensureNr2jOrganizationForProLead(lead.id);
        orgId = res?.organization?.id;
      }
      if (!orgId) throw new Error('Société NR2J introuvable');
      navigate(`/nr2j/clients/${orgId}`);
    } catch (error) {
      toast.error(error?.message || 'Impossible d’ouvrir la fiche société');
      setSelectedLead(lead);
    }
  };

  async function archiveOrg(org) {
    setMenuId(null);
    if (!window.confirm(`Archiver « ${organizationDisplayName(org)} » ?`)) return;
    try {
      await apiService.archiveNr2jOrganization(org.id);
      toast.success('Fiche archivée');
      await reloadAfterPipelineMutation();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    }
  }

  async function confirmDeleteOrg() {
    if (!deleteOrg?.id) return false;
    try {
      await apiService.deleteNr2jOrganization(deleteOrg.id);
      toast.success('Prospect supprimé définitivement');
      if (selectedLead?.organizationId === deleteOrg.id) {
        setSelectedLead(null);
      }
      await reloadAfterPipelineMutation();
      return true;
    } catch (error) {
      toast.error(error?.message || 'Échec de la suppression');
      return false;
    }
  }

  async function markLost(org) {
    setMenuId(null);
    if (!window.confirm(`Marquer « ${organizationDisplayName(org)} » comme perdu ?`)) return;
    try {
      await apiService.updateNr2jOrganization(org.id, { pipelineStage: 'PERDU' });
      toast.success('Prospect marqué comme perdu');
      await reloadAfterPipelineMutation();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    }
  }

  async function reactivateOrg(org) {
    setMenuId(null);
    try {
      await apiService.reactivateNr2jOrganization(org.id);
      toast.success('Fiche réactivée');
      await reloadAfterPipelineMutation();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    }
  }

  async function moveStage(org, nextStage) {
    const previousStage = org.pipelineStage;
    setMoveBusyId(org.id);
    setOrgs((prev) => prev.map((o) => (
      o.id === org.id ? { ...o, pipelineStage: nextStage } : o
    )));
    try {
      await apiService.updateNr2jOrganization(org.id, { pipelineStage: nextStage });
      toast.success(`Prospect passé en ${nr2jStageLabel(nextStage)}`);
      if (leadFilter !== 'ACTIVE' && leadFilter !== 'FOLLOWUP' && leadFilter !== nextStage) {
        setLeadFilter('ACTIVE');
        await loadOrgKpis();
        const leadsRes = await apiService.getProLeads();
        setLeads(leadsRes?.leads || []);
      } else {
        await reloadAfterPipelineMutation();
      }
    } catch (error) {
      setOrgs((prev) => prev.map((o) => (
        o.id === org.id ? { ...o, pipelineStage: previousStage } : o
      )));
      toast.error(error?.message || 'Impossible de changer l’étape');
    } finally {
      setMoveBusyId(null);
    }
  }

  function menuActionsFor(org) {
    const open = {
      id: 'open',
      label: 'Ouvrir la fiche',
      onClick: () => openOrganization(org.id),
    };
    const deleteAction = {
      id: 'delete',
      label: 'Supprimer définitivement',
      danger: true,
      onClick: () => {
        setMenuId(null);
        setDeleteOrg(org);
      },
    };
    if (org.commercialStatus === 'INACTIVE') {
      return [open, { id: 'reactivate', label: 'Réactiver', onClick: () => reactivateOrg(org) }];
    }
    if (org.commercialStatus === 'CLIENT') {
      return [open];
    }
    if (org.pipelineStage === 'GAGNE') {
      return [open, deleteAction];
    }
    if (org.pipelineStage === 'PERDU') {
      return [
        open,
        {
          id: 'reopen-pipeline',
          label: 'Réactiver la prospection',
          onClick: async () => {
            try {
              await apiService.updateNr2jOrganization(org.id, { pipelineStage: 'CONTACTE' });
              toast.success('Prospect remis en Contacté');
              await reloadAfterPipelineMutation();
            } catch (error) {
              toast.error(error?.message || 'Échec');
            }
          },
        },
        { id: 'archive', label: 'Archiver', onClick: () => archiveOrg(org) },
        deleteAction,
      ];
    }

    const stageActions = NR2J_KANBAN_STAGES
      .filter((col) => !col.match.includes(org.pipelineStage))
      .map((col) => ({
        id: `stage-${col.id}`,
        label: `Passer en ${col.label}`,
        onClick: () => moveStage(org, col.id),
      }));

    return [
      open,
      { id: 'edit', label: 'Modifier', onClick: () => openOrganization(org.id) },
      {
        id: 'task',
        label: 'Créer une tâche',
        onClick: () => {
          setCreateTaskForm({ title: '', dueAt: '', notes: '' });
          setCreateTaskOrg(org);
        },
      },
      {
        id: 'followup',
        label: 'Planifier une relance',
        onClick: () => {
          setFollowUpConflict(null);
          setFollowUpOrg(org);
        },
      },
      ...stageActions,
      { id: 'lost', label: 'Marquer comme perdu', onClick: () => markLost(org) },
      { id: 'archive', label: 'Archiver', onClick: () => archiveOrg(org) },
      deleteAction,
    ];
  }

  async function submitFollowUp(payload, updateExisting = false) {
    if (!followUpOrg?.id) return;
    setFollowUpBusy(true);
    try {
      await apiService.planNr2jFollowUp(followUpOrg.id, { ...payload, updateExisting });
      toast.success(updateExisting ? 'Relance modifiée' : 'Relance planifiée');
      setFollowUpOrg(null);
      setFollowUpConflict(null);
      await reloadAfterPipelineMutation();
    } catch (error) {
      const existingTask = error?.data?.error?.existingTask;
      if (existingTask || error?.data?.error?.code === 'FOLLOWUP_EXISTS') {
        setFollowUpConflict({ message: error.message, existingTask });
      } else {
        toast.error(error?.message || 'Échec');
      }
    } finally {
      setFollowUpBusy(false);
    }
  }

  async function submitCreateTask(e) {
    e.preventDefault();
    if (!createTaskOrg?.id || !createTaskForm.title.trim()) return;
    setCreateTaskBusy(true);
    try {
      await apiService.createNr2jTask(createTaskOrg.id, {
        title: createTaskForm.title.trim(),
        type: 'SUIVI',
        dueAt: createTaskForm.dueAt ? new Date(createTaskForm.dueAt).toISOString() : null,
        notes: createTaskForm.notes || null,
      });
      toast.success('Tâche créée');
      setCreateTaskOrg(null);
      await loadPipeline(leadFilter);
    } catch (error) {
      toast.error(error?.message || 'Échec');
    } finally {
      setCreateTaskBusy(false);
    }
  }

  const isSuperAdminSession = readAdminSessionUser()?.role === 'SUPER_ADMIN';
  const proPage = (pages || []).find((page) => page.slug === 'professionnels');
  const professionalPublished = Boolean(proPage?.publishedRevisionId);
  const publicUrl = buildProfessionalsPublicUrl();
  const liveBadge = professionalsPageLiveBadge(professionalPublished);

  const kpiCards = [
    { id: 'NOUVEAU', label: 'Nouveaux', value: orgKpis.nouveaux, filter: 'NOUVEAU' },
    { id: 'FOLLOWUP', label: 'À relancer', value: orgKpis.aRelancer, filter: 'FOLLOWUP' },
    { id: 'QUALIFIE', label: 'Qualifiés', value: orgKpis.qualifies, filter: 'QUALIFIE' },
    { id: 'GAGNE', label: 'Gagnés', value: orgKpis.gagnes, filter: 'GAGNE' },
    { id: 'PERDU', label: 'Perdus', value: orgKpis.perdus, filter: 'PERDU' },
  ];

  if (loading) {
    return (
      <div className="space-y-4 p-4" data-testid="prospection-page">
        {toasts.map((item) => (
          <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
        ))}
        <p className="text-sm text-gray-500">Chargement de la prospection…</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="prospection-page">
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}

      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Prospection</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Prospects actifs NR2J. Les clients convertis vivent dans Clients NR2J — même société, même historique.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Ajouter un prospect
        </button>
      </header>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="nr2j-prospection-kpis">
        {kpiCards.map((card) => (
          <KpiCard
            key={card.id}
            label={card.label}
            value={card.value}
            active={leadFilter === card.filter}
            onClick={() => setLeadFilter(card.filter)}
          />
        ))}
        <KpiCard
          label="Conversion"
          value={formatPercent(orgKpis.tauxConversion)}
          active={conversionOpen}
          onClick={() => setConversionOpen(true)}
        />
      </div>

      {isSuperAdminSession ? (
      <SettingsCard
        icon={Target}
        title="Acquisition"
        subtitle="Lien public à diffuser — capture UTM recommandée (?utm_source=instagram)."
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className={liveBadge.className}>{liveBadge.label}</span>
        </div>
        <SettingsReadOnlyField label="URL publique" value={publicUrl} locked lockIcon={Lock} />
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={async () => {
              try {
                await copyProfessionalsPublicUrl(publicUrl, navigator.clipboard);
                toast.success('Lien copié');
              } catch {
                toast.error('Impossible de copier le lien');
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <Copy className="h-4 w-4 text-gray-500" />
            Copier le lien
          </button>
          <button
            type="button"
            disabled={!professionalPublished}
            onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ExternalLink className="h-4 w-4 text-gray-500" />
            Ouvrir la page
          </button>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Réglages footer / email professionnel : Paramètres → Site public → Acquisition &amp; Prospection.
        </p>
      </SettingsCard>
      ) : null}

      <SettingsCard icon={Target} title="Pipeline prospects" subtitle="Prospects actifs uniquement — hors clients et archives.">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex rounded-xl border border-gray-200 bg-white p-0.5" data-testid="nr2j-view-toggle">
            <button
              type="button"
              onClick={() => setView('list')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${
                effectiveView === 'list' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <List className="h-3.5 w-3.5" />
              Liste
            </button>
            <button
              type="button"
              disabled={isHistory}
              onClick={() => setView('kanban')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${
                effectiveView === 'kanban' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              } disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Kanban
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isHistory ? (
              <p className="text-xs text-gray-500">Kanban réservé aux prospects actifs.</p>
            ) : null}
            {ownerFilter.showOwner ? (
              <Nr2jOwnerFilterSelect
                value={ownerFilter.value}
                onChange={ownerFilter.setValue}
                options={ownerFilter.options}
              />
            ) : null}
          </div>
        </div>

        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Actifs</p>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {ACTIVE_FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setLeadFilter(item.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                leadFilter === item.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Historique</p>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {HISTORY_FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setLeadFilter(item.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                leadFilter === item.id ? 'bg-gray-800 text-white' : 'bg-gray-50 text-gray-600 border border-gray-200'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {effectiveView === 'kanban' ? (
          <Nr2jProspectionKanban
            organizations={orgs}
            menuId={menuId}
            onMenuToggle={(id) => setMenuId((cur) => (cur === id ? null : id))}
            onMenuClose={() => setMenuId(null)}
            menuActionsFor={menuActionsFor}
            onOpenOrganization={openOrganization}
            onMoveStage={moveStage}
            busyId={moveBusyId}
            showOwner={ownerFilter.showOwner}
            ownerLinkable={ownerFilter.canOpenMember}
            viewerId={ownerFilter.viewerId}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm" data-testid="nr2j-prospection-pipeline">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3">Société</th>
                  {ownerFilter.showOwner ? <th className="py-2 pr-3">Commercial</th> : null}
                  <th className="py-2 pr-3">Ville</th>
                  <th className="py-2 pr-3">Étape</th>
                  <th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">Prochaine action</th>
                  <th className="py-2 pr-3">Tâches</th>
                  <th className="py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => {
                  const next = formatProspectNextAction(org);
                  return (
                    <tr
                      key={org.id}
                      className="cursor-pointer border-b border-gray-50 hover:bg-gray-50"
                      onClick={() => openOrganization(org.id)}
                    >
                      <td className="py-2 pr-3">
                        <span className="font-medium text-gray-900">{organizationDisplayName(org)}</span>
                      </td>
                      {ownerFilter.showOwner ? (
                        <td className="py-2 pr-3">
                          <Nr2jOwnerCell
                            owner={org.ownerUser}
                            linkable={ownerFilter.canOpenMember}
                            viewerId={ownerFilter.viewerId}
                          />
                        </td>
                      ) : null}
                      <td className="py-2 pr-3">{org.ville || '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${stageBadgeClass(org.pipelineStage)}`}>
                          {nr2jStageLabel(org.pipelineStage)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-gray-600">
                        {org.primaryContact
                          ? `${org.primaryContact.prenom || ''} ${org.primaryContact.nom || ''}`.trim() || org.primaryContact.email
                          : (org.emailPrincipal || '—')}
                      </td>
                      <td className={`py-2 pr-3 text-xs ${next.overdue ? 'font-medium text-rose-700' : 'text-gray-600'}`}>
                        {next.text}
                      </td>
                      <td className="py-2 pr-3">{org.openTaskCount ?? 0}</td>
                      <td className="py-2" onClick={(e) => e.stopPropagation()}>
                        <Nr2jProspectRowMenu
                          org={org}
                          open={menuId === org.id}
                          onToggle={() => setMenuId((id) => (id === org.id ? null : org.id))}
                          onClose={() => setMenuId(null)}
                          actions={menuActionsFor(org)}
                        />
                      </td>
                    </tr>
                  );
                })}
                {orgs.length === 0 ? (
                  <tr>
                    <td colSpan={ownerFilter.showOwner ? 8 : 7} className="py-4 text-sm text-gray-500">
                      Aucun prospect pour ce filtre.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>

      {selectedLead ? (
        <ProLeadDrawer
          lead={selectedLead}
          busy={leadBusy}
          onClose={() => setSelectedLead(null)}
          onOpenOrganization={() => openFromLead(selectedLead)}
          onSave={async (patch) => {
            setLeadBusy(true);
            try {
              const updated = await apiService.updateProLead(selectedLead.id, patch);
              setLeads((prev) => prev.map((lead) => (lead.id === selectedLead.id ? updated : lead)));
              setSelectedLead(updated);
              toast.success('Prospect mis à jour');
            } finally {
              setLeadBusy(false);
            }
          }}
        />
      ) : null}

      <AddProspectModal
        open={addOpen}
        busy={addBusy}
        onClose={() => setAddOpen(false)}
        onSubmit={async (payload) => {
          setAddBusy(true);
          try {
            const { organization } = await createNr2jProspectFromModal(apiService, payload);
            setAddOpen(false);
            toast.success('Prospect ajouté');
            await loadOrgKpis();
            await loadPipeline(leadFilter);
            if (organization?.id) {
              navigate(`/nr2j/clients/${organization.id}`);
            }
          } finally {
            setAddBusy(false);
          }
        }}
      />

      <Nr2jPlanFollowUpModal
        open={Boolean(followUpOrg)}
        busy={followUpBusy}
        organizationName={followUpOrg ? organizationDisplayName(followUpOrg) : ''}
        existingConflict={followUpConflict}
        onClose={() => {
          setFollowUpOrg(null);
          setFollowUpConflict(null);
        }}
        onSubmit={(payload) => submitFollowUp(payload, false)}
        onUpdateExisting={(payload) => submitFollowUp(payload, true)}
      />

      {createTaskOrg ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/30" aria-label="Fermer" onClick={() => setCreateTaskOrg(null)} />
          <form
            onSubmit={submitCreateTask}
            className="relative w-full max-w-md space-y-3 rounded-xl bg-white p-5 shadow-xl"
          >
            <h3 className="text-lg font-semibold text-gray-900">Créer une tâche</h3>
            <p className="text-sm text-gray-500">{organizationDisplayName(createTaskOrg)}</p>
            <input
              required
              placeholder="Titre"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={createTaskForm.title}
              onChange={(e) => setCreateTaskForm((f) => ({ ...f, title: e.target.value }))}
            />
            <input
              type="datetime-local"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={createTaskForm.dueAt}
              onChange={(e) => setCreateTaskForm((f) => ({ ...f, dueAt: e.target.value }))}
            />
            <textarea
              rows={2}
              placeholder="Notes (optionnel)"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={createTaskForm.notes}
              onChange={(e) => setCreateTaskForm((f) => ({ ...f, notes: e.target.value }))}
            />
            <div className="flex gap-2">
              <button type="submit" disabled={createTaskBusy} className="flex-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
                Créer
              </button>
              <button type="button" onClick={() => setCreateTaskOrg(null)} className="rounded-lg border px-3 py-2 text-sm">
                Annuler
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {conversionOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="nr2j-conversion-detail">
          <button type="button" className="absolute inset-0 bg-black/30" aria-label="Fermer" onClick={() => setConversionOpen(false)} />
          <div className="relative w-full max-w-sm space-y-3 rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Taux de conversion</h3>
            <p className="text-3xl font-semibold text-gray-900">{formatPercent(orgKpis.tauxConversion)}</p>
            <dl className="space-y-1 text-sm text-gray-700">
              <div className="flex justify-between gap-4">
                <dt>Gagnés</dt>
                <dd className="font-medium">{orgKpis.gagnes}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>Perdus</dt>
                <dd className="font-medium">{orgKpis.perdus}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>Population utilisée</dt>
                <dd className="font-medium">{orgKpis.total}</dd>
              </div>
            </dl>
            <p className="text-xs text-gray-500">
              Formule : Gagnés ÷ total des organisations (prospects ouverts + gagnés + perdus).
            </p>
            <button
              type="button"
              onClick={() => setConversionOpen(false)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(deleteOrg)}
        onCancel={() => setDeleteOrg(null)}
        onConfirm={confirmDeleteOrg}
        tone="danger"
        title="Supprimer définitivement ce prospect ?"
        description="Cette action est irréversible. La société et ses données de prospection seront définitivement supprimées."
        confirmLabel="Supprimer définitivement"
        cancelLabel="Annuler"
      />
    </div>
  );
}

function KpiCard({ label, value, active = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`nr2j-kpi-${label}`}
      className={`rounded-2xl border px-3 py-3 text-left shadow-sm transition ${
        active
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
          : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
    </button>
  );
}
